<script module lang="ts">
	import { rowActions } from '@repo/commons/components'

	const ROW_ACTIONS = [rowActions.DUPLICATE, rowActions.EDIT, rowActions.DELETE]
</script>

<script lang="ts">
	import { ActionButtonsIcons, DataTable } from '@repo/commons/components'
	import type { ReferenceTableRow, TableColumn, TableData } from '@repo/commons/models'
	import {
		filterRowCellsByQuery,
		getPrimaryKeyColumns,
		getTableRowId,
		toColumnKey,
		toOrderedColumnNames,
		toRowCells
	} from '@repo/commons/utils'
	import type { ResourceSchema } from '@semanticedge/persistence-service-noheaders-client/src'
	import { t } from 'svelte-i18n'

	type Props = {
		rows: readonly ReferenceTableRow[]
		schema: ResourceSchema | undefined
		searchQuery: string
		selectedRows: TableData[]
		onSelectionChange: (selection: TableData[]) => void
		onRowAction: (action: rowActions, rowIndex: number) => void
	}

	const { rows, schema, searchQuery, selectedRows, onSelectionChange, onRowAction }: Props =
		$props()

	const columnNames = $derived(toOrderedColumnNames(schema?.columns, schema?.columnOrder))
	const primaryKeyColumns = $derived(getPrimaryKeyColumns(schema?.columns))

	const rowViewModels = $derived(
		rows.map((row, index) => ({ ...toRowCells(row, index, columnNames), actions: ROW_ACTIONS }))
	)

	const visibleRows = $derived(filterRowCellsByQuery(rowViewModels, searchQuery))

	const handleRowAction = (action: rowActions, rowId: string): void => {
		onRowAction(action, Number.parseInt(rowId, 10))
	}

	const columns = $derived([
		...columnNames.map((name) => {
			const isPrimaryKey = primaryKeyColumns.includes(name)

			return {
				key: toColumnKey(name),
				label: name,
				maxWidth: '280px',
				labelIcon: isPrimaryKey ? 'key' : undefined,
				labelIconLabel: isPrimaryKey ? $t('ReferenceTableRowsView.primaryKeyColumn') : undefined
			}
		}),
		{
			key: 'actions',
			label: $t('Table.actions'),
			width: '140px',
			component: ActionButtonsIcons,
			componentProps: { onActionHandler: handleRowAction }
		}
	] as unknown as TableColumn[])
</script>

<DataTable
	selectable={true}
	{selectedRows}
	{onSelectionChange}
	getRowId={getTableRowId}
	horizontalScroll={true}
	pinEdgeColumns={true}
	minColumnWidth="160px"
	data={visibleRows}
	{columns}
	emptyText={$t('ReferenceTableRowsView.empty')}
	sortable={false}
	paginated={false}
/>
